import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getCategories,
    getSubCategories,
    getCountries,
    getItemTypes,
    getLocations,
    getShips,
    getAirports,
    getEstablishmentTypes,
    getEstablishmentTypeQuestions,
    sendFoundItemRequest,
    sendLostItemRequest,
    getQuestions,
    toggleEmailCommunication,
} from "./generaleWebDataAsyncThunk";
import { Category, Country, Location, Question, Ship, SubCategory, Airport, EstablishmentType, EstablishmentTypeQuestion } from "../../../types";

export type Status = {
    status: "idle" | "loading" | "succeeded" | "failed";
}

export type CountryState = {
    countries: Country[];
    country: Country | null;
    status: Status["status"];
    error: string | null;
};

export type LocationState = {
    locations: Location[];
    location: Location | null;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
};

export type CategoryState = {
    categories: Category[];
    category: Category | null;
    status: Status["status"];
    error: string | null;
};

export type SubCategoryState = {
    subCategories: SubCategory[];
    subCategory: SubCategory | null;
    status: Status["status"];
    error: string | null;
};


export type QuestionsState = {
    questions: Question[];
    question: Question | null;
    status: Status["status"];
    error: string | null;
};

export type ShipState = {
    ships: Ship[];
    ship: Ship | null;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
};

export type AirportState = {
    airports: Airport[];
    airport: Airport | null;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
};

export type EstablishmentTypeState = {
    establishmentTypes: EstablishmentType[];
    establishmentType: EstablishmentType | null;
    status: Status["status"];
    error: string | null;
};

export type EstablishmentTypeQuestionState = {
    establishmentTypeQuestions: EstablishmentTypeQuestion[];
    establishmentTypeQuestion: EstablishmentTypeQuestion | null;
    status: Status["status"];
    error: string | null;
};

// export type ItemTypeState = {
//     itemTypes: ItemType[];
//     itemType: IItemType | null;
//     status: "idle" | "loading" | "succeeded" | "failed";
//     error: string | null;
// };

export type LostItemRequestState = {
    lostItemFormData: Record<string, any>;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
};

export type FoundItemRequestState = {
    foundItemFormData: Record<string, any>;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
};

export type State = {
    countryState: CountryState;
    shipState: ShipState;
    airportState: AirportState;
    establishmentTypeState: EstablishmentTypeState;
    establishmentTypeQuestionState: EstablishmentTypeQuestionState;
    locationState: LocationState;
    // itemTypeState: ItemTypeState;
    lostItemRequestState: LostItemRequestState;
    foundItemRequestState: FoundItemRequestState;
    isSuccessModalVisible: boolean;
    trackingId: string;
    categoryState: CategoryState;
    subCategoryState: SubCategoryState
    questionsState: QuestionsState
    toggleEmailCommunication: any
};


const initialState: State = {
    countryState: {
        countries: [],
        country: null,
        status: "idle",
        error: null,
    },
    shipState: {
        ships: [],
        ship: null,
        status: "idle",
        error: null,
    },
    airportState: {
        airports: [],
        airport: null,
        status: "idle",
        error: null,
    },
    establishmentTypeState: {
        establishmentTypes: [],
        establishmentType: null,
        status: "idle",
        error: null,
    },
    establishmentTypeQuestionState: {
        establishmentTypeQuestions: [],
        establishmentTypeQuestion: null,
        status: "idle",
        error: null,
    },
    locationState: {
        locations: [],
        location: null,
        status: "idle",
        error: null,
    },
    toggleEmailCommunication: {
        toggleEmailCommunication: null,
        status: "idle",
        error: null,
    },
    // itemTypeState: {
    //     itemTypes: [],
    //     itemType: null,
    //     status: "idle",
    //     error: null,
    // },
    lostItemRequestState: {
        lostItemFormData: {},
        status: "idle",
        error: null,
    },
    foundItemRequestState: {
        foundItemFormData: {},
        status: "idle",
        error: null,
    },
    categoryState: {
        categories: [],
        category: null,
        status: "idle",
        error: null,
    },
    subCategoryState: {
        subCategories: [],
        subCategory: null,
        status: "idle",
        error: null,
    },
    questionsState: {
        questions: [],
        question: null,
        status: "idle",
        error: null,
    },
    isSuccessModalVisible: false,
    trackingId: "",
};


interface SetKeyValuePayload {
    key: keyof State;
    value: any;
}

const generaleWebDataSlice = createSlice({
    name: "lostAndFoundItemRequestData",
    initialState,
    reducers: {
        addValueLostItemFormData: (state, action) => {
            state.lostItemRequestState.lostItemFormData = {
                ...state.lostItemRequestState.lostItemFormData,
                ...action.payload,
            };
        },
        resetLostItemFormState: (state) => {
            state.lostItemRequestState = {
                lostItemFormData: {},
                status: "idle",
                error: null,
            }
        },
        resetFoundItemFormState: (state) => {
            state.foundItemRequestState = {
                foundItemFormData: {},
                status: "idle",
                error: null,
            }
        },
        setKeyValue: (state, action: PayloadAction<SetKeyValuePayload>) => {
            const { key, value } = action.payload;
            (state[key] as any) = value;
        },
    },
    extraReducers: (builder) => {
        builder
            // Handle getCountries actions
            .addCase(getCountries.pending, (state) => {
                state.countryState.status = "loading";
                state.countryState.error = null;
            })
            .addCase(getCountries.fulfilled, (state, action) => {
                state.countryState.status = "succeeded";
                state.countryState.countries = action.payload;
            })
            .addCase(getCountries.rejected, (state, action) => {
                state.countryState.status = "failed";
                state.countryState.error =
                    action.payload || "Failed to fetch countries";
            })
            // Handle getShips actions
            .addCase(getShips.pending, (state) => {
                state.shipState.status = "loading";
                state.shipState.error = null;
            })
            .addCase(getShips.fulfilled, (state, action) => {
                state.shipState.status = "succeeded";
                state.shipState.ships = action.payload;
            })
            .addCase(getShips.rejected, (state, action) => {
                state.shipState.status = "failed";
                state.shipState.error = action.payload || "Failed to fetch ships";
            })
            // Handle getAirports actions
            .addCase(getAirports.pending, (state) => {
                state.airportState.status = "loading";
                state.airportState.error = null;
            })
            .addCase(getAirports.fulfilled, (state, action) => {
                state.airportState.status = "succeeded";
                state.airportState.airports = action.payload;
            })
            .addCase(getAirports.rejected, (state, action) => {
                state.airportState.status = "failed";
                state.airportState.error = action.payload || "Failed to fetch airports";
            })
            // Handle getEstablishmentType actions
            .addCase(getEstablishmentTypes.pending, (state) => {
                state.establishmentTypeState.status = "loading";
                state.establishmentTypeState.error = null;
            })
            .addCase(getEstablishmentTypes.fulfilled, (state, action) => {
                state.establishmentTypeState.status = "succeeded";
                state.establishmentTypeState.establishmentTypes = action.payload;
            })
            .addCase(getEstablishmentTypes.rejected, (state, action) => {
                state.establishmentTypeState.status = "failed";
                state.establishmentTypeState.error = action.payload || "Failed to fetch establishmentType";
            })
            // Handle getCustomerQuestionType actions
            .addCase(getEstablishmentTypeQuestions.pending, (state) => {
                state.establishmentTypeQuestionState.status = "loading";
                state.establishmentTypeQuestionState.error = null;
            })
            .addCase(getEstablishmentTypeQuestions.fulfilled, (state, action) => {
                state.establishmentTypeQuestionState.status = "succeeded";
                state.establishmentTypeQuestionState.establishmentTypeQuestions = action.payload.data;
            })
            .addCase(getEstablishmentTypeQuestions.rejected, (state, action) => {
                state.establishmentTypeQuestionState.status = "failed";
                state.establishmentTypeQuestionState.error = action.payload || "Failed to fetch establishmentType";
            })
            // Handle getLocations actions
            .addCase(getLocations.pending, (state) => {
                state.locationState.status = "loading";
                state.locationState.error = null;
            })
            .addCase(getLocations.fulfilled, (state, action) => {
                state.locationState.status = "succeeded";
                state.locationState.locations = action.payload;
            })
            .addCase(getLocations.rejected, (state, action) => {
                state.locationState.status = "failed";
                state.locationState.error =
                    action.payload || "Failed to fetch locations";
            })
            // Handle getItems actions
            // .addCase(getItemTypes.pending, (state) => {
            //     state.itemTypeState.status = "loading";
            //     state.itemTypeState.error = null;
            // })
            // .addCase(getItemTypes.fulfilled, (state, action) => {
            //     state.itemTypeState.status = "succeeded";
            //     state.itemTypeState.itemTypes = action.payload;
            // })
            // .addCase(getItemTypes.rejected, (state, action) => {
            //     state.itemTypeState.status = "failed";
            //     state.itemTypeState.error =
            //         action.payload || "Failed to fetch item types";
            // })
            // Handle sendLostItemRequest actions
            .addCase(sendLostItemRequest.pending, (state) => {
                state.lostItemRequestState.status = "loading";
                state.lostItemRequestState.error = null;
            })
            .addCase(sendLostItemRequest.fulfilled, (state, action) => {
                state.lostItemRequestState.status = "succeeded";
                state.isSuccessModalVisible = true;
                state.trackingId = action.payload.trackingId;
            })
            .addCase(sendLostItemRequest.rejected, (state, action) => {
                state.lostItemRequestState.status = "failed";
                state.lostItemRequestState.error =
                    action.payload || "Failed to send lost item request";
            })
            // sendFoundItemRequest
            .addCase(sendFoundItemRequest.pending, (state) => {
                state.foundItemRequestState.status = "loading";
                state.foundItemRequestState.error = null;
            })
            .addCase(sendFoundItemRequest.fulfilled, (state, action) => {
                state.foundItemRequestState.status = "succeeded";
                state.isSuccessModalVisible = true;
                state.trackingId = action.payload.trackingId;
            })
            .addCase(sendFoundItemRequest.rejected, (state, action) => {
                state.foundItemRequestState.status = "failed";
                state.foundItemRequestState.error =
                    action.payload || "Failed to send found item request";
            })
            // Get Categories
            .addCase(getCategories.pending, (state) => {
                state.categoryState.status = 'loading';
                state.categoryState.error = null;
            })
            .addCase(getCategories.fulfilled, (state, action: PayloadAction<Category[]>) => {
                state.categoryState.status = 'succeeded';
                state.categoryState.categories = action.payload;
            })
            .addCase(getCategories.rejected, (state, action) => {
                state.categoryState.status = 'failed';
                state.categoryState.error = action.payload || 'Failed to fetch categories';
            })
            // Get Sub Categories
            .addCase(getSubCategories.pending, (state) => {
                state.subCategoryState.status = 'loading';
                state.subCategoryState.error = null;
            })
            .addCase(getSubCategories.fulfilled, (state, action: PayloadAction<SubCategory[]>) => {
                state.subCategoryState.status = 'succeeded';
                state.subCategoryState.subCategories = action.payload;
            })
            .addCase(getSubCategories.rejected, (state, action) => {
                state.subCategoryState.status = 'failed';
                state.subCategoryState.error = action.payload || 'Failed to fetch categories';
            })
            // Get Questions
            .addCase(getQuestions.pending, (state) => {
                state.questionsState.status = 'loading';
                state.questionsState.error = null;
            })
            .addCase(getQuestions.fulfilled, (state, action) => {
                state.questionsState.status = 'succeeded';
                state.questionsState.questions = action.payload.data;
            })
            .addCase(getQuestions.rejected, (state, action) => {
                state.questionsState.status = 'failed';
                state.questionsState.error = action.payload || 'Failed to fetch Question';
            })
            // Handle toggleEmailCommunication actions
            .addCase(toggleEmailCommunication.pending, (state) => {
                state.toggleEmailCommunication.status = "loading";
                state.toggleEmailCommunication.error = null;
            })
            .addCase(toggleEmailCommunication.fulfilled, (state, action) => {
                state.toggleEmailCommunication.status = "succeeded";
                state.toggleEmailCommunication.toggleEmailCommunication = action.payload;
            })
            .addCase(toggleEmailCommunication.rejected, (state, action) => {
                state.toggleEmailCommunication.status = "failed";
                state.toggleEmailCommunication.error =
                    action.payload || "Failed to fetch locations";
            })
    },
});

export const { addValueLostItemFormData, setKeyValue, resetLostItemFormState, resetFoundItemFormState } = generaleWebDataSlice.actions;
export default generaleWebDataSlice.reducer;
